<script setup lang="ts">
import { type Ref as VueRef, ref, watch } from 'vue'
import { useDomainValidation } from '@/composables/src/useDomainValidation'
import { AutoFormLabel } from '@/foundation/auto-form'
import type { FieldProps } from '@/foundation/auto-form/interface'
import { FormControl, FormDescription, FormField, FormItem, FormMessage } from '@/foundation/form'
import { TagsInput, TagsInputInput, TagsInputItem, TagsInputItemDelete } from '@/foundation/tags-input'

const props = defineProps<FieldProps & { duplicateDomains?: string[] }>()

const localDomains = ref<string[]>([])
const capturedComponentField: VueRef<any> = ref(null) // To store componentField from slotProps

const {
  immediateErrorMessage,
  persistedInvalidTextForPlaceholder,
  handleDomainUpdate,
  displayDomains, // Use this for the template
} = useDomainValidation(localDomains)

// Watch for changes from the AutoForm model (via capturedComponentField) and update localDomains
watch(
  () => capturedComponentField.value?.modelValue,
  (newValFromForm) => {
    if (!capturedComponentField.value) return
    const formValue = Array.isArray(newValFromForm)
      ? newValFromForm
      : typeof newValFromForm === 'string' && newValFromForm.length > 0
        ? [newValFromForm]
        : []
    const formValueLowercase = formValue.map((d) => d.toLowerCase())

    if (JSON.stringify(formValueLowercase) !== JSON.stringify(localDomains.value)) {
      localDomains.value = formValueLowercase
    }
  },
  { deep: true },
)

// Watch for changes in localDomains (made by the composable) and update the AutoForm model
watch(
  localDomains,
  (newLocalVal) => {
    if (capturedComponentField.value?.['onUpdate:modelValue']) {
      const currentFormValue = capturedComponentField.value.modelValue
      const formValueArray = Array.isArray(currentFormValue)
        ? currentFormValue
        : typeof currentFormValue === 'string' && currentFormValue.length > 0
          ? [currentFormValue]
          : []
      const formValueLowercase = formValueArray.map((d) => d.toLowerCase())

      if (JSON.stringify(newLocalVal) !== JSON.stringify(formValueLowercase)) {
        capturedComponentField.value['onUpdate:modelValue'](newLocalVal)
      }
    }
  },
  { deep: true },
)
</script>

<template>
  <FormField v-slot="localSlotProps" :name="fieldName">
    {{
      (() => {
        capturedComponentField = localSlotProps.componentField
        return ''
      })()
    }}
    <FormItem v-bind="$attrs">
      <AutoFormLabel v-if="!config?.hideLabel" :required="required">
        {{ config?.label }}
      </AutoFormLabel>
      <FormControl>
        <TagsInput v-if="displayDomains.length" v-model="localDomains" class="!bg-transparent !p-0 !border-none">
          <div class="flex flex-wrap gap-2">
            <TagsInputItem
              v-for="item in displayDomains"
              :key="item"
              :value="item"
              class="pl-2 rounded-full bg-blue-100 text-blue-800"
            >
              {{ item }}
              <TagsInputItemDelete class="ml-1 text-blue-800" />
            </TagsInputItem>
          </div>
        </TagsInput>
        <TagsInput
          :model-value="displayDomains"
          class="!mt-2"
          :class="config?.inputProps?.class"
          delimiter=","
          @update:model-value="handleDomainUpdate"
        >
          <TagsInputInput
            :placeholder="
              persistedInvalidTextForPlaceholder ||
              config?.inputProps?.placeholder ||
              'Type domains and press Enter or comma (,) to add. You can add multiple domains.'
            "
          />
        </TagsInput>

        <div v-if="immediateErrorMessage" class="text-red-500 text-sm mt-1">
          {{ immediateErrorMessage }}
        </div>
      </FormControl>
      <FormDescription v-if="!immediateErrorMessage">
        {{ config?.description }}
      </FormDescription>
      <FormMessage />
    </FormItem>
  </FormField>
</template>
